import { Fragment, ReactNode } from 'react'

import { ArrowPosition, TooltipWidth } from './Tooltip.types'

export default function Tooltip({
  arrowPosition,
  children,
  containerClassName,
  showTooltip,
  width = 'normal',
  showBorder,
  borderColor,
}: {
  arrowPosition: ArrowPosition
  children: ReactNode
  containerClassName?: string
  showTooltip: boolean
  width?: TooltipWidth | string
  showBorder?: boolean
  borderColor?: string
}): React.ReactElement {
  const sharedClassName = 'absolute transform'
  const borderClassNames = showBorder ? `border-2 ${borderColor || 'border-purple'}` : ''
  const { arrow, arrowBorder, body, container, arrowTop } = getTooltipClassName(
    arrowPosition,
    borderColor,
    containerClassName,
  )
  const tooltipWidth = getTooltipWidth(width)

  if (showTooltip) {
    return (
      <div className={`isolate ${sharedClassName} ${container}`}>
        {showBorder && <div className={`z-9 ${sharedClassName} ${arrowBorder}`} />}
        <div className={`z-30 ${sharedClassName} ${arrowTop}`} />
        <div className={`z-20 ${sharedClassName} ${arrow}`} />
        <div className={`z-10 ${sharedClassName} ${body} ${tooltipWidth} ${borderClassNames}`}>{children}</div>
      </div>
    )
  } else {
    return <Fragment />
  }
}

function getTooltipClassName(
  arrowPosition: ArrowPosition,
  borderColor?: string,
  containerClassName?: string,
): {
  arrow: string
  arrowBorder: string
  borderColor?: string
  body: string
  container: string
  arrowTop?: string
} {
  const sharedArrowClassName = 'w-4 h-4'
  const sharedArrowColor = 'bg-gray-off-white'
  const sharedArrowBorderColor = `${borderColor || 'border-purple'}`
  const sharedArrowBorderMediumSlate = `${borderColor || 'bg-blue-medium-slate'}`
  const sharedArrowBorderIndigo = `${borderColor || 'bg-blue-indigo'}`

  const sharedBodyClassName =
    'p-3 rounded border border-gray-light shadow-experimental bg-gray-off-white text-black text-xxs leading-4'

  switch (arrowPosition) {
    case 'bottom-left':
      return {
        container: `-top-3 left-1/2 -translate-x-1/2 ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px -translate-y-px scale-[2.5]`,
        body: `${sharedBodyClassName} -left-6 -translate-y-full top-px`,
      }

    case 'bottom-center':
      return {
        container: `-top-4 left-1/2 -translate-x-1/2 ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px scale-[2.5]`,
        body: `${sharedBodyClassName} left-1/2 -translate-x-1/2 -translate-y-full top-px`,
      }

    case 'bottom-right':
      return {
        container: `-top-4 left-1/2 -translate-x-1/2 ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-down left-1/2 -translate-x-1/2 -translate-y-px scale-[2.5]`,
        body: `${sharedBodyClassName} -right-6 -translate-y-full top-px`,
      }

    case 'top-right':
      return {
        container: `top-10 left-1/2 -translate-x-1/2 -translate-y-full ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-up left-1/2 -translate-x-1/2 -translate-y-full`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-up left-1/2 -translate-x-1/2 -translate-y-full scale-[2.5]`,
        body: `${sharedBodyClassName} -right-6 -translate-y-1`,
      }

    case 'middle-left':
      return {
        container: `top-1/2 right-0 -translate-y-1/2 ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-left top-1/2 translate-x-px -translate-y-1/2`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-left top-1/2 translate-x-px -translate-y-1/2 scale-[2.5]`,
        body: `${sharedBodyClassName} top-1/2 -right-3.5 translate-x-full -translate-y-1/2`,
      }

    case 'middle-right':
      return {
        container: `top-1/2 left-0 -translate-y-1/2 ${containerClassName}`,
        arrow: `${sharedArrowClassName} ${sharedArrowColor} triangle-right top-1/2 -left-px -translate-x-full -translate-y-1/2`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderColor} triangle-right top-1/2 -left-px -translate-x-full -translate-y-1/2 scale-[2.5]`,
        body: `${sharedBodyClassName} top-1/2 -left-3.5 -translate-x-full -translate-y-1/2`,
      }

    case 'bottom-center-high':
      return {
        container: `-top-10 left-0 -translate-x-1/2 ${containerClassName}`,
        arrowTop: `${sharedArrowClassName} ${sharedArrowColor} w-9 triangle-down left-1/3 -translate-x-1/2 -translate-y-1`,
        arrow: `${sharedArrowClassName} ${sharedArrowBorderMediumSlate} w-9 triangle-down left-1/3 -translate-x-1/2 -translate-y-px`,
        arrowBorder: `${sharedArrowClassName} ${sharedArrowBorderIndigo} w-9 triangle-down left-1/2 -translate-x-1/2 translate-y-0.5 scale-[2.5]`,
        body: `p-3 rounded border border-blue-medium-slate shadow-task-indigo bg-gray-off-white text-black text-xxs leading-4 p-1 pt-2.5 left-1/2 -translate-x-1/2 -translate-y-full top-px`,
      }

    default:
      return {
        container: '',
        body: '',
        arrow: '',
        arrowBorder: '',
      }
  }
}

function getTooltipWidth(width: TooltipWidth | string): string {
  switch (width) {
    case 'tightest':
      return 'w-48'

    case 'tighter':
      return 'w-56'

    case 'normal':
      return 'w-64'

    case 'wider':
      return 'w-72'

    case 'widest':
      return 'w-80'

    case 'larger':
      return 'w-106.25'

    default:
      return width
  }
}
